import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type {
  NotificationItem,
  NotificationListResponse,
  NotificationPreference,
  NotificationPreferenceUpdate,
} from '../types/notification'

export function useNotifications(onlyUnread = false) {
  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', { onlyUnread }],
    queryFn: async () => {
      const res = await api.get('/notifications', { params: { only_unread: onlyUnread } })
      return res.data
    },
    refetchInterval: 15_000,
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.patch(`/notifications/${id}/read`)
      return res.data as NotificationItem
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    // One server call clears EVERY unread — not just the ≤50 the bell loaded —
    // so the badge can always reach zero.
    mutationFn: async () => {
      const res = await api.post('/notifications/read-all')
      return res.data as { marked: number }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useNotificationPreferences() {
  return useQuery<NotificationPreference[]>({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const res = await api.get('/notifications/preferences')
      // Defensive: some upstream proxies/wrappers occasionally wrap list
      // responses in {items: [...]}; accept either shape rather than
      // crash downstream consumers (Profile.tsx:findPref).
      if (Array.isArray(res.data)) return res.data
      if (Array.isArray((res.data as { items?: unknown[] })?.items)) {
        return (res.data as { items: NotificationPreference[] }).items
      }
      return []
    },
  })
}

export function useUpdateNotificationPreference() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      type,
      eventKey,
      body,
    }: {
      type: string
      eventKey?: string
      body: NotificationPreferenceUpdate
    }) => {
      const params: Record<string, string> = {}
      if (eventKey) params.event_key = eventKey
      const res = await api.put(`/notifications/preferences/${type}`, body, { params })
      return res.data as NotificationPreference
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-preferences'] }),
  })
}
